import { DetailedOrder } from '../domain/types';
import { COLLECTIONS } from '../domain/collections';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';
import { PAGINATION } from '../domain/constants';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  limit
} from "firebase/firestore";
import { BaseRepository, BaseEntity } from '../domain/services/BaseRepository';

interface OrderEntity extends DetailedOrder, BaseEntity {}

class OrderSearch extends BaseRepository<OrderEntity> {
  protected collectionName = COLLECTIONS.ORDERS;

  // Error counter to limit log spam
  private orderErrorCount = 0;
  private readonly MAX_ORDER_ERROR_LOGS = 3;

  async getPendingOrdersWithoutPartner(): Promise<DetailedOrder[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where("status", "==", "approved"),
        orderBy("createdAt", "desc"),
        limit(PAGINATION.BULK_QUERY_LIMIT)
      );

      const snapshot = await getDocs(q);

      // Reset error count on success
      this.orderErrorCount = 0;

      return snapshot.docs
        .filter(doc => {
          const data = doc.data();
          return !data.partnerId || data.partnerId === null || data.partnerId === '';
        })
        .slice(0, PAGINATION.FILTERED_RESULTS_LIMIT)
        .map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id,
            items: data.items || [],
            statusLabel: data.statusLabel || 'Pendiente',
            timeline: data.timeline || []
          } as DetailedOrder;
        });
    } catch (error) {
      // Limit error logging to avoid console spam
      if (this.orderErrorCount < this.MAX_ORDER_ERROR_LOGS) {
        logger.error('[OrderSearch] Error fetching pending orders without partner', error);
        this.orderErrorCount++;
        if (this.orderErrorCount === this.MAX_ORDER_ERROR_LOGS) {
          logger.warn('[OrderSearch] Suppressing further order search error logs');
        }
      }
      return [];
    }
  }

  async searchOrdersByUserInfo(searchTerm: string): Promise<DetailedOrder[]> {
    try {
      const results: DetailedOrder[] = [];
      const seenIds = new Set<string>();

      // Search by email
      if (searchTerm.includes('@')) {
        const emailQuery = query(
          collection(db, this.collectionName),
          where("email", "==", searchTerm.toLowerCase()),
          limit(PAGINATION.SEARCH_RESULTS_LIMIT)
        );
        const emailSnapshot = await getDocs(emailQuery);
        emailSnapshot.docs.forEach(doc => {
          if (!seenIds.has(doc.id)) {
            results.push({ ...doc.data(), id: doc.id } as DetailedOrder);
            seenIds.add(doc.id);
          }
        });
      }

      // Search by order ID
      if (searchTerm.length >= 6) {
        try {
          const docRef = doc(db, this.collectionName, searchTerm);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists() && !seenIds.has(docSnap.id)) {
            results.push({ ...docSnap.data(), id: docSnap.id } as DetailedOrder);
          }
        } catch {
          // Order ID not found
        }
      }

      // Reset error count on success
      this.orderErrorCount = 0;

      return results;
    } catch (error) {
      // Limit error logging to avoid console spam
      if (this.orderErrorCount < this.MAX_ORDER_ERROR_LOGS) {
        logger.error('[OrderSearch] Error searching orders', error);
        this.orderErrorCount++;
        if (this.orderErrorCount === this.MAX_ORDER_ERROR_LOGS) {
          logger.warn('[OrderSearch] Suppressing further order search error logs');
        }
      }
      return [];
    }
  }

  async getOrdersByDateRange(startDate: string, endDate: string): Promise<DetailedOrder[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where("createdAt", ">=", startDate),
        where("createdAt", "<=", endDate),
        orderBy("createdAt", "desc"),
        limit(PAGINATION.DEFAULT_PAGE_SIZE)
      );

      const snapshot = await getDocs(q);

      // Reset error count on success
      this.orderErrorCount = 0;

      return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as DetailedOrder));
    } catch (error) {
      // Limit error logging to avoid console spam
      if (this.orderErrorCount < this.MAX_ORDER_ERROR_LOGS) {
        logger.error('[OrderSearch] Error fetching orders by date range', error);
        this.orderErrorCount++;
        if (this.orderErrorCount === this.MAX_ORDER_ERROR_LOGS) {
          logger.warn('[OrderSearch] Suppressing further order search error logs');
        }
      }
      return [];
    }
  }

  async getOrdersByStatus(status: string, limitCount: number = PAGINATION.DEFAULT_PAGE_SIZE): Promise<DetailedOrder[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where("status", "==", status),
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);

      // Reset error count on success
      this.orderErrorCount = 0;

      return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as DetailedOrder));
    } catch (error) {
      // Limit error logging to avoid console spam
      if (this.orderErrorCount < this.MAX_ORDER_ERROR_LOGS) {
        logger.error('[OrderSearch] Error fetching orders by status', error);
        this.orderErrorCount++;
        if (this.orderErrorCount === this.MAX_ORDER_ERROR_LOGS) {
          logger.warn('[OrderSearch] Suppressing further order search error logs');
        }
      }
      return [];
    }
  }
}

export const orderSearch = new OrderSearch();
